import React, { useCallback, useEffect, useRef } from "react";

import type { PDFDocumentProxy } from "pdfjs-dist";

import { useInView, usePdfDocument, usePdfPageCanvas } from "utils";

import { Spin, Typography } from "components";

type PdfDocumentPageProps = { pdf: PDFDocumentProxy; pageNumber: number; isInitialPage: boolean };

const PdfDocumentPage = ({ pdf, pageNumber, isInitialPage }: PdfDocumentPageProps) => {
  // A page is drawn once it scrolls into view, so a long PDF isn't downloaded and drawn up front.
  const { ref: inViewRef, inView } = useInView({ triggerOnce: true });
  const pageRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const { isRendering, hasError } = usePdfPageCanvas({ pdf, pageNumber: inView ? pageNumber : undefined, canvasRef });

  const setPageRef = useCallback(
    (node: HTMLDivElement | null) => {
      pageRef.current = node;
      inViewRef(node);
    },
    [inViewRef]
  );

  useEffect(() => {
    if (isInitialPage) pageRef.current?.scrollIntoView?.({ block: "start" });
  }, [isInitialPage]);

  return (
    <div ref={setPageRef} className="pdf-document-viewer-page">
      {hasError ? (
        <Typography variant="paragraph-m">This page could not be loaded.</Typography>
      ) : (
        <Spin spinning={isRendering}>
          <canvas ref={canvasRef} className={isRendering ? "pdf-document-viewer-canvas-pending" : undefined} />
        </Spin>
      )}
      <Typography variant="paragraph-s" color="grey-600">
        Page {pageNumber} of {pdf.numPages}
      </Typography>
    </div>
  );
};

type PdfDocumentViewerProps = {
  /** URL of the PDF, e.g. a presigned S3 URL. The server must allow range requests from the browser. */
  pdfUrl: string;
  /** The page to scroll to once the PDF loaded, from 1. */
  initialPageNumber?: number;
};

/** Shows every page of a PDF, one below the other. */
export const PdfDocumentViewer = ({ pdfUrl, initialPageNumber = 1 }: PdfDocumentViewerProps) => {
  const { pdf, hasError } = usePdfDocument(pdfUrl);

  if (hasError) return <Typography variant="paragraph-m">The document could not be loaded.</Typography>;
  if (!pdf) return <Spin className="pdf-document-viewer-loading" />;

  return (
    <div className="pdf-document-viewer">
      {Array.from({ length: pdf.numPages }, (_, index) => (
        <PdfDocumentPage key={index + 1} pdf={pdf} pageNumber={index + 1} isInitialPage={index + 1 === initialPageNumber} />
      ))}
    </div>
  );
};
